import * as R from 'ramda';

import {
  exceedsADOTStock,
  roundADOTQuantity,
} from '@atom/components/common/requests/adotQuantityUtilities';
import { getADOTReplenishmentInputsDisabled } from '@atom/components/common/requests/adotReplenishmentUtilities';
import { AssetBudget, AssetCategory } from '@atom/types/asset';
import {
  AssetRequestCreateInput,
  AssetRequestPurchaseUnitInput,
  AssetRequestType,
} from '@atom/types/assetRequest';
import { UserGroup } from '@atom/types/userGroups';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { TotalRemainingState } from '../sddotCreate/sddotCreateUtilities';

export const getADOTDefaultPurchaseUnit = (
  budget: AssetBudget,
): AssetRequestPurchaseUnitInput => {
  const defaultUnit = (budget?.conversionUnits ?? []).find(
    conversionUnit => conversionUnit.isDefault && conversionUnit.active,
  );

  return defaultUnit
    ? { unit: defaultUnit.unit, conversionNumber: defaultUnit.conversionNumber }
    : null;
};

const ADOT_APPROVAL_GROUP_PREFIX_LENGTH = 4;

const getADOTApprovalGroupPrefix = (name: string): string =>
  name.slice(0, ADOT_APPROVAL_GROUP_PREFIX_LENGTH);

// Approval groups belong to the Transfer From item's immediate parent folder:
// the first four characters of the group name match those of the folder name.
export const isADOTApprovalGroup = (
  categories: AssetCategory[] = [],
  group: UserGroup,
): boolean => {
  const folder = R.last(categories);

  return (
    !!folder &&
    getADOTApprovalGroupPrefix(group.name) ===
      getADOTApprovalGroupPrefix(folder.name)
  );
};

export const getADOTAdjustedStockLevel = (
  totalRemaining: number,
  quantity: number,
): number => roundADOTQuantity(Number(totalRemaining) + Number(quantity));

export const getADOTCreateDisabled = (
  createInputs: AssetRequestCreateInput,
  totals: TotalRemainingState,
): boolean => {
  const {
    quantity,
    quantityOrdered,
    reason,
    comments,
    fromAssetId,
    assignedTo,
    assignedToGroups,
    replenishment,
  } = createInputs;
  const {
    totalRemaining,
    fromTotalRemaining,
    totalRemainingLoading,
    fromTotalRemainingLoading,
  } = totals;

  const disabledRules = {
    [AssetRequestType.ADOT_INVENTORY_TRANSFER]:
      totalRemainingLoading ||
      fromTotalRemainingLoading ||
      isNilOrEmpty(fromAssetId) ||
      isNilOrEmpty(quantityOrdered) ||
      exceedsADOTStock(fromTotalRemaining, quantityOrdered) ||
      (isNilOrEmpty(assignedTo) && isNilOrEmpty(assignedToGroups)),
    [AssetRequestType.ADOT_ADJUSTMENT]:
      totalRemainingLoading ||
      isNilOrEmpty(quantity) ||
      getADOTAdjustedStockLevel(totalRemaining, quantity) < 0 ||
      isNilOrEmpty(reason) ||
      isNilOrEmpty(comments),
    [AssetRequestType.ADOT_REPLENISHMENT]:
      !(Number(quantityOrdered) > 0) ||
      getADOTReplenishmentInputsDisabled(replenishment),
  };

  return disabledRules[createInputs?.type] ?? false;
};
